import { AssetType } from '@amzn/fire-tv-campaign-automation-service';
import { KatFileUploadStatus } from '@amzn/katal-components/es/file-upload';
import { KatFileUpload, KatInput } from '@amzn/katal-react';
import { AssetTransformationHandler } from 'src/handlers/AssetTransformationHandler';
import { AssetValidationHandler } from 'src/handlers/validation/AssetValidationHandler';
import { AssetDetails, AssetState, AssetStatus, IAssetInputHandler, InputType } from 'src/types';
import { DimensionSpecs, VideoLengthSpecs } from 'src/utils/Types';

export interface AssetInputProps {
  /**
   * Label for the input component
   */
  label: string;

  /**
   * Options to enable/disable different types of asset inputs
   * Key is the input type (UPLOAD, URL, LOGO_TEXT)
   * Value is a boolean indicating whether to show TransformationTemplate dropdown
   */
  assetInputOptions: Partial<Record<InputType, boolean>>;

  /**
   * Handler for asset input operations
   */
  assetInputHandler: IAssetInputHandler;

  /**
   * Accepted file types for upload
   */
  acceptedFileTypes: KatFileUpload.Props['accept'];

  /**
   * Maximum file size in bytes
   */
  maxFileSizeInBytes: KatFileUpload.Props['maxSize'];

  /**
   * Error message to display
   */
  error?: string;

  /**
   * Current status of the asset
   */
  status: AssetStatus;

  /**
   * Type of the Asset
   */
  assetType: AssetType;

  /**
   * Asset details containing key and file information
   */
  assetDetails: AssetDetails;

  /**
   * Handler for asset transformation operations
   */
  assetTransformationHandler: AssetTransformationHandler;

  /**
   * Handler for asset validation operations
   */
  assetValidationHandler?: AssetValidationHandler;
}

export interface FileUploadState {
  fileUploadState: {
    status: KatFileUploadStatus;
    error: string;
  };
}

export interface ConfirmationDialogProps {
  isVisible: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export interface StatusIndicatorProps {
  error?: string;
  status?: AssetStatus;
}

export interface UrlInputProps {
  /**
   * Current URL value
   */
  url: string;
  /**
   * Handler for URL input changes
   */
  onChange: (event: KatInput.ChangeEvent) => void;
  /**
   * Handler for fetch button clicks
   */
  onFetch: () => void;
  /**
   * Handler for clear button clicks
   */
  onClear: () => void;
  /**
   * Flag indicating if the URL is valid
   */
  isValidUrl: boolean;
  /**
   * Flag indicating if a fetch operation is in progress
   */
  isFetching: boolean;
  /**
   * Flag to disable the clear URL button
   */
  disableClearUrlButton: boolean;
  /**
   * Current status of the asset
   */
  status?: AssetStatus;
  /**
   * Error message to display below the input
   */
  error?: string;
}

type RadioButtonInput = { label: string; value: InputType };

export const INPUT_OPTIONS: Partial<Record<InputType, RadioButtonInput>> = {
  [InputType.UPLOAD]: { label: 'Upload', value: InputType.UPLOAD },
  [InputType.URL]: { label: 'URL', value: InputType.URL },
} as const;

/**
 * Interface for validation properties of assets
 */
export interface ValidationProps {
  /**
   * The maximum file size in Bytes
   */
  maxFileSizeInBytes: number;
  /**
   * The required aspect ratio for the asset
   */
  aspectRatio?: number;
  /**
   * Dimension specifications for the asset
   */
  dimensionSpecification: DimensionSpecs;
  /**
   * Video length specifications for video assets
   */
  videoLengthSpecs?: VideoLengthSpecs;
}
